import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AuthCard } from '../../../components/auth/AuthCard';
import { AuthCardTitle } from '../../../components/auth/AuthCardTitle';
import { AuthLayout } from '../../../components/auth/AuthLayout';
import { ButtonFooter } from '../../../components/auth/ButtonFooter';
import { getRoute } from '../../../config/routes.config';
import { loginForm } from '../../../forms/auth/login.form';
import { LoginMutation, useLoginMutation, useVerifyTwoFactorLoginMutation } from '../../../generated/graphql';
import { useOnComplete } from '../../../hooks/use-on-complete';
import { useSubmitHandler } from '../../../releox-engine/hooks/use-submit-handler/use-submit-handler';
import { AppFormGenerator } from '../../../releox-engine/react/app-form-generator/AppFormGenerator';
import { Button } from '../../../releox-engine/react/buttons/Button';

export const LoginScene = () => {
  const { t } = useTranslation('LoginScene');
  const navigate = useNavigate();
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorSessionToken, setTwoFactorSessionToken] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');

  const loginUser = useCallback((data: LoginMutation) => {
    if (data.login.token) {
      localStorage.setItem('token', data.login.token);
    }
  }, []);

  const onComplete = useOnComplete({
    route: 'DASHBOARD',
    callback: loginUser,
    notification: 'none',
  });

  const [login, { loading }] = useLoginMutation({
    onCompleted: (data) => {
      if (data.login.requiresTwoFactor && data.login.twoFactorToken) {
        setTwoFactorRequired(true);
        setTwoFactorSessionToken(data.login.twoFactorToken);
      } else {
        onComplete(data);
      }
    },
  });

  const [verifyTwoFactor, { loading: verifyLoading }] = useVerifyTwoFactorLoginMutation({
    onCompleted: (data) => {
      if (data.verifyTwoFactorLogin.token) {
        localStorage.setItem('token', data.verifyTwoFactorLogin.token);
        navigate(getRoute('DASHBOARD'));
      }
    },
  });

  const handleLogin = useSubmitHandler(login);

  const handleTwoFactorVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (twoFactorSessionToken && verificationCode.length === 6) {
      verifyTwoFactor({
        variables: {
          twoFactorToken: twoFactorSessionToken,
          token: verificationCode,
        },
      });
    } else {
      toast.error(String(t('invalidTokenLength')));
    }
  };

  if (twoFactorRequired) {
    return (
      <AuthLayout title={t('twoFactorVerification')}>
        <AuthCard>
          <div className="mb-5">
            <AuthCardTitle>{t('twoFactorVerification')}</AuthCardTitle>
            <p className="text-sm text-gray-600 mt-2">{t('enter2faCode')}</p>
          </div>
          <form onSubmit={handleTwoFactorVerify} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('verificationCode')}
              </label>
              <input
                type="text"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-between">
              <Button
                type="button"
                onClick={() => {
                  setTwoFactorRequired(false);
                  setTwoFactorSessionToken(null);
                  setVerificationCode('');
                }}
              >
                {t('back')}
              </Button>
              <Button
                type="submit"
                disabled={verificationCode.length !== 6}
                loading={verifyLoading}
              >
                {t('verify')}
              </Button>
            </div>
          </form>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('login')}>
      <AuthCard>
        <div className="mb-5">
          <AuthCardTitle>{t('login')}</AuthCardTitle>
        </div>
        <AppFormGenerator
          form={loginForm}
          onSubmit={handleLogin}
          wrapperClassNames="space-y-3"
          FooterElement={
            <ButtonFooter
              buttonText={t('login')}
              loading={loading}
              links={[
                {
                  to: getRoute('FORGOT'),
                  text: t('forgotPassword'),
                },
              ]}
            />
          }
        />
      </AuthCard>
    </AuthLayout>
  );
};
